import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import CustomerTabs from "../../components/CustomerTabs";
import PendingQuotesModal from "../../components/Modal/Quote/ApprovalQuoteModal";
import api from "../../../utils/axios";
import Swal from "sweetalert2";
import QuoteTable from "../../components/Quote/QuoteTableList";
import { hasOrgPermissionKey } from "../../../utils/orgAccessPermissions";

const QUOTE_LIST_STATE_KEY_PREFIX = "quoteListState";
const QUOTE_LIST_FOCUS_KEY_PREFIX = "quoteListFocusDocId";

export default function QuoteListPage() {
  const { id: AR_Id } = useParams();
  const navigate = useNavigate();
  const stateStorageKey = `${QUOTE_LIST_STATE_KEY_PREFIX}:${AR_Id || "unknown"}`;
  const focusStorageKey = `${QUOTE_LIST_FOCUS_KEY_PREFIX}:${AR_Id || "unknown"}`;

  const loadSavedSearch = () => {
    try {
      const raw = localStorage.getItem(stateStorageKey);
      if (!raw) return "";
      const parsed = JSON.parse(raw);
      return parsed?.search || "";
    } catch {
      return "";
    }
  };

  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [search, setSearch] = useState(loadSavedSearch);
  const [focusedDocId, setFocusedDocId] = useState(
    localStorage.getItem(focusStorageKey) || null
  );

  const pendingCount = quotes.filter((q) => q.Doc_Status === 2).length;
  const [openPendingModal, setOpenPendingModal] = useState(false);

  const canQuoteCreate = hasOrgPermissionKey("page.sale_quote.create");
  const canQuoteUpdate = hasOrgPermissionKey("page.sale_quote.update");
  const canQuoteDelete = hasOrgPermissionKey("page.sale_quote.delete");

  //  โหลดข้อมูล
  const fetchQuotes = async (keyword = "", isInitial = false) => {
    try {
      if (isInitial) setLoading(true);
      const params = new URLSearchParams({ AR_Id });
      const role = String(localStorage.getItem("role") || "").trim();
      const empId = String(localStorage.getItem("emp_id") || "").trim();
      const isAdmin = role === "Admin";
      if (!isAdmin && empId) {
        params.set("emp_id", empId);
      }
      if (keyword) params.set("keyword", keyword);
      const res = await api.get(`/get-quotes?${params}`);
      setQuotes(res.data?.result || []);

      if (isInitial) {
        const custRes = await api.get(`/get-tb-set-ar1/${AR_Id}`);
        setCustomer(custRes.data?.result?.recordset?.[0] || null);
      }
    } catch (error) {
      console.error("โหลดข้อมูลใบเสนอราคาไม่สำเร็จ:", error);
      Swal.fire("ผิดพลาด!", "โหลดข้อมูลไม่สำเร็จ", "error");
    } finally {
      if (isInitial) setLoading(false);
    }
  };

  useEffect(() => {
    if (!AR_Id) return;
    const restoredSearch = loadSavedSearch();
    setSearch(restoredSearch);
    setFocusedDocId(localStorage.getItem(focusStorageKey) || null);
    fetchQuotes(restoredSearch, true);
  }, [AR_Id]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(stateStorageKey);
      const prev = raw ? JSON.parse(raw) : {};
      localStorage.setItem(
        stateStorageKey,
        JSON.stringify({
          ...prev,
          search,
        })
      );
    } catch {}
  }, [search, stateStorageKey]);

  const handleSearchChange = (value) => {
    setSearch(value);
    fetchQuotes(value);
  };

  const handleDelete = async (quote) => {
    try {
      await api.delete(`/delete-quote/${quote.Doc_Id}`);
      fetchQuotes(search);
    } catch (error) {
      console.error("ลบใบเสนอราคาไม่สำเร็จ:", error);
      Swal.fire("ผิดพลาด!", "เกิดข้อผิดพลาดในการลบข้อมูล", "error");
    }
  };

  const handleEdit = (quote) => {
    const docId = quote?.Doc_Id != null ? String(quote.Doc_Id) : null;
    setFocusedDocId(docId);
    if (docId) localStorage.setItem(focusStorageKey, docId);
    navigate(`/quote/edit/${quote.Doc_Id}?AR_Id=${AR_Id}`);
  };

  

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar />
      <main
        className="flex-1 h-screen bg-gray-50 flex flex-col"
        style={{
          height: "100vh",
          overflowX: "hidden",
          overflowY: "hidden",
          marginLeft: "var(--sidebar-width)",
        }}
      >
        <Topbar
          breadcrumbs={[
            { label: "ลูกค้า", link: "/sale" },
            { label: "รายการลูกค้า", link: "/sale" },
            { label: "ใบเสนอราคา", link: `/quotelist/${AR_Id}` },
          ]}
        />

        <div className="sticky top-14 z-20 bg-white">
          <CustomerTabs customer={customer} />
        </div>

        <PendingQuotesModal
          isOpen={openPendingModal}
          onClose={() => setOpenPendingModal(false)}
          quotes={quotes.filter((q) => q.Doc_Status === 2)}
        />


        <div className="flex-1 min-h-0 overflow-y-auto pl-2 pr-8 pb-8 pt-16">

          {loading ? (
            <p className="text-gray-500">กำลังโหลดข้อมูล...</p>
          ) : (
            <>
              <QuoteTable
                quotes={quotes}
                onEdit={handleEdit}
                onDelete={handleDelete}
                fillAvailable
                showCustomer={false}
                allowCreate={canQuoteCreate}
                allowEdit={canQuoteUpdate}
                allowDelete={canQuoteDelete}
                isAdmin={false}
                search={search}
                onSearchChange={handleSearchChange}
                focusedDocId={focusedDocId}
                onFocusQuote={(q) => {
                  const docId = q?.Doc_Id != null ? String(q.Doc_Id) : null;
                  setFocusedDocId(docId);
                  if (docId) localStorage.setItem(focusStorageKey, docId);
                }}
                stateStorageKey={stateStorageKey}
              />

            </>
          )}
        </div>
      </main>
    </div>
  );
}
